import type { UppKonvaLabelFontSize } from '../../model/upp-konva-types';

/**
 * Colors and strokes for the floor plan to match the host app (light / dark).
 */
export interface UppFloorPlanTheme {
  roomFill: string;
  roomStroke: string;
  roomStrokeWidth: number;
  /** Dash pattern (px) for the hollow room perimeter rectangle; empty = solid stroke. */
  roomPerimeterDash: number[];
  /** Lines, arrow heads, and labels for width/height dimensions (v3-style). */
  roomDimensionColor: string;
  roomDimensionFontSize: number;
  elementFill: string;
  elementStroke: string;
  elementStrokeWidth: number;
  /** Table tabletop inner fill when `options.attention` is true (warning tone). */
  tableAttentionFill: string;
  /** Table / chair outer stroke when attention is true (warning). */
  tableAttentionStroke: string;
  /** Inner chair rect stroke when attention is true (warning). */
  tableAttentionChairInner: string;
  /** Success-tone highlight (table `attention` + `attentionTone === 'SUCCESS'`). */
  tableAttentionSuccessFill: string;
  tableAttentionSuccessStroke: string;
  tableAttentionSuccessChairInner: string;
  /** Danger-tone highlight (table `attention` + `attentionTone === 'DANGER'`). */
  tableAttentionDangerFill: string;
  tableAttentionDangerStroke: string;
  tableAttentionDangerChairInner: string;
  /**
   * Opaque fill for **closed** interior wall polygons (`UppFloorPlanVertex` groups with `closed`).
   * Keep opaque so overlapping regions do not look darker (same colour stacks visually as one layer).
   */
  interiorClosedPolygonFill: string;
  labelColor: string;
  labelFontSize: number;
  /** Background of the element label badge (white / light translucent by default; anchor inset from bbox top-right). */
  elementLabelBadgeFill: string;
  /** Border around the label badge. */
  elementLabelBadgeStroke: string;
  elementLabelBadgeStrokeWidth: number;
}

export const UPPKONVA_LIGHTTHEME: UppFloorPlanTheme = {
  roomFill: '#f4f6f8',
  roomStroke: '#9aa7b2',
  roomStrokeWidth: 2,
  roomPerimeterDash: [6, 6],
  roomDimensionColor: '#7d8a96',
  roomDimensionFontSize: 12,
  elementFill: '#d7e3f0',
  elementStroke: '#3d5a80',
  elementStrokeWidth: 1.5,
  tableAttentionFill: '#fde68a',
  tableAttentionStroke: '#ca8a04',
  tableAttentionChairInner: 'rgba(202, 138, 4, 0.55)',
  tableAttentionSuccessFill: '#bbf7d0',
  tableAttentionSuccessStroke: '#15803d',
  tableAttentionSuccessChairInner: 'rgba(21, 128, 61, 0.5)',
  tableAttentionDangerFill: '#fecaca',
  tableAttentionDangerStroke: '#b91c1c',
  tableAttentionDangerChairInner: 'rgba(185, 28, 28, 0.5)',
  interiorClosedPolygonFill: '#d4e8fc',
  labelColor: '#1b2633',
  labelFontSize: 12,
  /** Slightly more opaque so strokes under the badge don’t wash out dark label ink. */
  elementLabelBadgeFill: 'rgba(255, 255, 255, 0.86)',
  elementLabelBadgeStroke: 'rgba(140, 155, 170, 0.55)',
  elementLabelBadgeStrokeWidth: 1,
};

export const UPPKONVA_DARKTHEME: UppFloorPlanTheme = {
  roomFill: '#1e2430',
  /** Dashed perimeter on dark floor — lighter slate so the frame stays readable. */
  roomStroke: '#9db0c4',
  roomStrokeWidth: 2.5,
  roomPerimeterDash: [6, 6],
  /** Dimension labels sit in outer margin (often light host padding): dark ink for contrast, not pale grey. */
  roomDimensionColor: '#33485a',
  roomDimensionFontSize: 13,
  /** Table/bar fill vs room floor: stronger separation than the old #2f3d4d on #1e2430. */
  elementFill: '#3a4d62',
  /** Brighter, thicker strokes — old #8fa3b6 @ 1.5px faded on dark floor. */
  elementStroke: '#c5d8ea',
  elementStrokeWidth: 2,
  tableAttentionFill: 'rgba(255, 196, 9, 0.38)',
  tableAttentionStroke: '#fbbf24',
  tableAttentionChairInner: 'rgba(251, 191, 36, 0.55)',
  tableAttentionSuccessFill: 'rgba(34, 197, 94, 0.35)',
  tableAttentionSuccessStroke: '#4ade80',
  tableAttentionSuccessChairInner: 'rgba(74, 222, 128, 0.55)',
  tableAttentionDangerFill: 'rgba(248, 113, 113, 0.38)',
  tableAttentionDangerStroke: '#f87171',
  tableAttentionDangerChairInner: 'rgba(248, 113, 113, 0.55)',
  interiorClosedPolygonFill: '#354c62',
  labelColor: '#e8eef4',
  labelFontSize: 12,
  /**
   * Dark translucent panel (not light frosted glass): a light `rgba(255,…,α)` badge lets bright
   * element strokes bleed through and kills contrast with `labelColor`. Keeps a hint of transparency.
   */
  elementLabelBadgeFill: 'rgba(22, 28, 38, 0.88)',
  elementLabelBadgeStroke: 'rgba(165, 185, 205, 0.42)',
  elementLabelBadgeStrokeWidth: 1,
};

/** Used inside the stage when resolving partial `theme` input; not part of the public package API. */
export function mergeUppFloorPlanTheme(
  partial: Partial<UppFloorPlanTheme> | null | undefined,
  base: UppFloorPlanTheme,
): UppFloorPlanTheme {
  return { ...base, ...partial };
}

/**
 * Applies {@link UppFloorPlanPreferences.labelSize} to the already-merged theme's `labelFontSize` (light/dark + overrides).
 */
export function resolveUppFloorPlanThemeLabelFontSize(
  theme: UppFloorPlanTheme,
  labelSize: UppKonvaLabelFontSize,
): UppFloorPlanTheme {
  const base = theme.labelFontSize;
  const next =
    labelSize === 'SMALL'
      ? Math.max(8, Math.round(base * 0.82))
      : labelSize === 'LARGE'
        ? Math.round(base * 1.28)
        : base;
  return { ...theme, labelFontSize: next };
}

/** Stroke width (px) for interior wall polylines; at least **3** so walls stay readable on screen. Internal to painting. */
export function floorPlanInteriorWallStrokeWidthPx(theme: UppFloorPlanTheme): number {
  return Math.max(3, theme.elementStrokeWidth);
}
